import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  DispatchDetailBatch,
  DispatchDetailStop,
  DispatchRiderSnapshot,
} from '@/services/api/dispatches';

interface AssignDeferredModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The held batch the stops belong to. Null = closed. */
  batch: DispatchDetailBatch | null;
  /** Stops being assigned — one (per-row) or all of the batch (assign-all). */
  stops: DispatchDetailStop[];
  /** Riders on the dispatch — all are valid targets (a held batch has no owner). */
  riders: DispatchRiderSnapshot[];
  onSubmit: (
    stopIds: string[],
    rider: DispatchRiderSnapshot
  ) => Promise<void>;
}

/**
 * Assign held (DEFERRED) stops onto a rider. Same shape as ReassignBatchModal
 * — a rider radio picker — but there's no source rider to transfer/swap from,
 * so it's a one-way assign. Lists the stops (address + waybills) up top so ops
 * can see exactly what's moving.
 */
const AssignDeferredModal: React.FC<AssignDeferredModalProps> = ({
  isOpen,
  onClose,
  batch,
  stops,
  riders,
  onSubmit,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedId(null);
  }, [isOpen]);

  const itemCount = stops.reduce((n, s) => n + s.itemCount, 0);
  const selectedRider = riders.find((r) => r.id === selectedId);
  const canSubmit = !submitting && batch != null && selectedRider != null;

  const handleSubmit = async () => {
    if (!selectedRider || stops.length === 0) return;
    setSubmitting(true);
    try {
      await onSubmit(
        stops.map((s) => s.id),
        selectedRider
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-user-shared-line" />}
      iconTone="primary"
      title={stops.length > 1 ? 'Assign batch ke rider' : 'Assign stop ke rider'}
      subtitle={
        batch
          ? `${batch.code} · ${stops.length} stop · ${itemCount} item`
          : undefined
      }
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting
              ? 'Meng-assign…'
              : stops.length > 1
              ? 'Assign semua'
              : 'Assign'}
          </Button>
        </div>
      }
    >
      {/* What's moving — stops with their address + waybills. */}
      <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        Stop yang dipindah
      </p>
      <div className="mb-4 max-h-52 overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {stops.map((s) => {
          const waybills = Array.from(new Set(s.items.map((it) => it.waybill)));
          return (
            <div key={s.id} className="px-3 py-2.5">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 text-[13px] font-semibold tracking-tight text-text-strong-950">
                  {s.receiverName || `Stop #${s.sequence}`}
                </p>
                <span className="flex-none text-[11px] text-text-sub-600 tabular-nums">
                  {s.itemCount} item
                </span>
              </div>
              <p className="mt-0.5 text-[12px] leading-snug text-text-sub-600">
                <i
                  className="ri-map-pin-line mr-1 text-[12px] align-[-1px] text-text-soft-400"
                  aria-hidden="true"
                />
                {s.destinationAddress || '—'}
                {s.zoneName ? ` · ${s.zoneName}` : ''}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {waybills.map((wb) => (
                  <span
                    key={wb}
                    className="rounded-md border border-stroke-soft-200 bg-bg-weak-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-text-sub-600"
                  >
                    {wb}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Target rider picker. */}
      <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        Assign ke rider
      </p>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {riders.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            Belum ada rider di dispatch ini — tambah lewat "Add riders".
          </div>
        ) : (
          riders.map((r) => {
            const checked = selectedId === r.id;
            return (
              <label
                key={r.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  checked ? 'bg-bg-weak-50 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                }`}
              >
                <input
                  type="radio"
                  name="assign-deferred-rider"
                  checked={checked}
                  onChange={() => setSelectedId(r.id)}
                  disabled={submitting}
                  className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                    {r.name}
                  </span>
                  <span className="block font-mono text-[11px] text-text-sub-600 tabular-nums">
                    {r.code}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </FormModal>
  );
};

export default AssignDeferredModal;
